// app/components/AdvancedChart/components/IndicatorPicker.tsx

import React from 'react'
import { showIndicatorsGuide } from './IndicatorGuide'

// We define the "IndicatorPickerProps" to outline how the parent
// will open/close the modal and pass a callback for selection.
interface IndicatorPickerProps {
  isOpen: boolean
  onClose: () => void
  onSelectIndicator: (indicatorName: string) => void
  activeIndicators?: string[] // List of currently active indicators
  onRemoveIndicator?: (indicatorName: string) => void // Callback to remove an indicator
}

/**
 * Baby-level explanation:
 * This component shows a native HTML select dropdown with indicator choices.
 * Works automatically in Flutter webviews without any bridges.
 */

// Add indicator data structure
const INDICATORS = [
  { id: 'RSI', name: 'RSI', category: 'Momentum' },
  { id: 'MACD', name: 'MACD', category: 'Momentum' },
  { id: 'Stochastic', name: 'Stochastic', category: 'Momentum' },
  { id: 'BollingerBands', name: 'Bollinger Bands', category: 'Volatility' },
  { id: 'ATR', name: 'ATR', category: 'Volatility' },
  { id: 'MFI', name: 'MFI', category: 'Volume' },
  { id: 'ForceIndex', name: 'Force Index', category: 'Volume' },
  { id: 'WMA', name: 'WMA', category: 'Trend' }
] as const

// Create a button component that triggers the native select
export const IndicatorPickerButton = React.memo(
  ({ onSelectIndicator, activeIndicators = [], onRemoveIndicator }: Omit<IndicatorPickerProps, 'isOpen' | 'onClose'>) => {
    const handleIndicatorChange = React.useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value
        if (value && value !== '') {
          if (value === 'how-it-works') {
            // Handle "How it works" guide
            showIndicatorsGuide()
          } else if (value.startsWith('remove:')) {
            // Handle removal
            const indicatorId = value.replace('remove:', '')
            onRemoveIndicator?.(indicatorId)
          } else {
            // Handle selection
            onSelectIndicator(value)
          }
        }
        // Reset select to empty value
        event.target.value = ''
      },
      [onSelectIndicator, onRemoveIndicator]
    )

    // Group indicators by category for better organization
    const groupedIndicators = React.useMemo(
      () =>
        INDICATORS.reduce(
          (acc, ind) => {
            const category = ind.category
            if (!acc[category]) {
              acc[category] = [ind]
            } else {
              acc[category].push(ind)
            }
            return acc
          },
          {} as Record<string, { id: string; name: string; category: string }[]>
        ),
      []
    )

    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          style={{
            background: 'transparent',
            color: '#fff',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center'
          }}
          onClick={e => {
            const select = e.currentTarget.nextElementSibling as HTMLSelectElement
            select?.click()
          }}
        >
          IND ▼
        </button>
        <select
          value=""
          onChange={handleIndicatorChange}
          style={{
            position: 'absolute',
            opacity: 0,
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            cursor: 'pointer'
          }}
        >
          <option value="">Select Indicator</option>
          
          {/* Add indicators grouped by category */}
          {Object.entries(groupedIndicators).map(([category, indicators]) => (
            <optgroup key={category} label={category}>
              {indicators.map(indicator => {
                const isActive = activeIndicators.includes(indicator.id)
                return (
                  <option 
                    key={indicator.id} 
                    value={indicator.id}
                    disabled={isActive}
                  >
                    {indicator.name} {isActive ? '[Active]' : ''}
                  </option>
                )
              })}
            </optgroup>
          ))}
          
          {/* Add removal options for active indicators */}
          {activeIndicators.length > 0 && (
            <optgroup label="Remove Active">
              {activeIndicators.map(indicatorId => {
                const indicator = INDICATORS.find(ind => ind.id === indicatorId)
                return indicator ? (
                  <option 
                    key={`remove:${indicator.id}`} 
                    value={`remove:${indicator.id}`}
                  >
                    Remove {indicator.name}
                  </option>
                ) : null
              })}
            </optgroup>
          )}
          
          {/* Add help option */}
          <optgroup label="Help">
            <option value="how-it-works">📚 How it works?</option>
          </optgroup>
        </select>
      </div>
    )
  }
)

// Keep the original IndicatorPicker for backward compatibility, but make it use the button
export const IndicatorPicker = React.memo(
  ({ isOpen, onClose, onSelectIndicator, activeIndicators = [], onRemoveIndicator }: IndicatorPickerProps) => {
    // For the modal version, just render nothing since we're using the button approach
    // This maintains backward compatibility
    return null
  }
)
